import { apiSlice } from './apiSlice';

export const categoryApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllCategories: builder.query({
      query: () => ({
        url: `/api/categories`,
        method: 'GET',
      }),
      transformResponse: (response) => response.categories, // directly return categories array
      providesTags: ['Categories'],
    }),
    getCategoryById: builder.query({
      query: (categoryId) => ({
        url: `/api/categories/${categoryId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.category, // directly return the category object
      providesTags: (result, error, categoryId) => [{ type: 'Category', id: categoryId }],
    }),
    getCategoriesByClientId: builder.query({
      query: (clientId) => ({
        url: `/api/clients/${clientId}/categories`,
        method: 'GET',
      }),
      transformResponse: (response) => response.categories,
      providesTags: ['CategoriesClients'],
    }),
    createCategory: builder.mutation({
      query: (newCategory) => ({
        url: `/api/categories`,
        method: 'POST',
        body: newCategory,
      }),
      invalidatesTags: [
        'CategoriesClients',
        'CategoriesClientsByCustomerId',
        'Categories',
        'Category',
        'CategoryClients',
        'CategoriesClientsGroups',
        'CategoriesClientsGroupsByCustomer',
        'GroupsByCategory',
        'SelectedGroupsByCategory',
      ],
    }),
    updateCategory: builder.mutation({
      query: ({ categoryId, ...updatedCategory }) => ({
        url: `/api/categories/${categoryId}`,
        method: 'PUT',
        body: updatedCategory,
      }),
      invalidatesTags: (result, error, { categoryId }) => [
        { type: 'CategoryClients', id: categoryId }, // Invalidate the specific category
        'CategoriesClients', // Invalidate the list of categories
        'Categories',
        'Category',
        'CategoriesClientsByCustomerId',
        'CategoriesClientsGroups',
        'CategoriesClientsGroupsByCustomer',
        'GroupsByCategory',
        'SelectedGroupsByCategory',
      ],
    }),
    getAllCategoriesClients: builder.query({
      query: () => ({
        url: `/api/categories-clients`,
        method: 'GET',
      }),
      transformResponse: (response) => response.categories,
      providesTags: ['CategoriesClients'],
    }),
    getCategoryClientsById: builder.query({
      query: (categoryId) => ({
        url: `/api/categories-clients/${categoryId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.category,
      providesTags: (result, error, categoryId) => [{ type: 'CategoryClients', id: categoryId }],
    }),
    getAllCategoriesClientsByCustomerId: builder.query({
      query: (customerId) => ({
        url: `/api/customers/${customerId}/categories-clients`,
        method: 'GET',
      }),
      transformResponse: (response) => response.categories,
      providesTags: ['CategoriesClientsByCustomerId'],
    }),
    getAllCategoriesClientsGroups: builder.query({
      query: () => ({
        url: '/api/categories/clients/groups',
        method: 'GET',
      }),
      transformResponse: (response) => response.categories,
      providesTags: ['CategoriesClientsGroups'],
    }),

    getAllCategoriesClientsGroupsByCustomer: builder.query({
      query: (customerId) => ({
        url: `/api/customers/${customerId}/categories-clients-groups`,
        method: 'GET',
      }),
      transformResponse: (response) => response.categories,
      providesTags: (result, error, customerId) => [{ type: 'CategoriesClientsGroupsByCustomer', id: customerId }],
    }),
    getGroupsByCategory: builder.query({
      query: (categoryId: string) => `/api/categories/${categoryId}/groups`,
      transformResponse: (response) => response.groups,
      providesTags: (result, error, categoryId) => [{ type: 'GroupsByCategory', id: categoryId }],
    }),
    getSelectedGroupsByCategory: builder.query({
      query: (categoryId: string) => `/api/categories/${categoryId}/selected-groups`,
      transformResponse: (response) => response.groups,
      providesTags: (result, error, categoryId) => [{ type: 'SelectedGroupsByCategory', id: categoryId }],
    }),
    updateCategoryGroups: builder.mutation({
      query: ({ categoryId, groupIds }) => ({
        url: `/api/categories/${categoryId}/groups`,
        method: 'PUT',
        body: { group_ids: groupIds },
      }),
      invalidatesTags: (result, error, { categoryId }) => [
        { type: 'GroupsByCategory', id: categoryId },
        { type: 'SelectedGroupsByCategory', id: categoryId },
        'CategoriesClientsGroups',
        'CategoriesClientsGroupsByCustomer',
        'GroupsByCategory',
        'SelectedGroupsByCategory',
      ],
    }),
    getCategoriesByUserId: builder.query({
      query: (userId) => ({
        url: `/api/categories/user/${userId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.categories,
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),
    getAllCategoriesGroupsByClientId: builder.query({
      query: (clientId) => ({
        url: `/api/clients/${clientId}/categories-groups`,
        method: 'GET',
      }),
      transformResponse: (response) => response.categories,
      providesTags: (result, error, clientId) => [{ type: 'CategoriesClientsGroups', id: clientId }],
    }),
  }),
});

export const {
  useGetAllCategoriesQuery,
  useGetCategoryByIdQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useGetCategoriesByClientIdQuery,
  useGetAllCategoriesClientsQuery,
  useGetCategoryClientsByIdQuery,
  useGetAllCategoriesClientsByCustomerIdQuery,
  useGetAllCategoriesClientsGroupsQuery,
  useGetAllCategoriesClientsGroupsByCustomerQuery,
  useGetGroupsByCategoryQuery,
  useGetSelectedGroupsByCategoryQuery,
  useUpdateCategoryGroupsMutation,
  useGetCategoriesByUserIdQuery,
  useGetAllCategoriesGroupsByClientIdQuery,
} = categoryApiSlice;
